/**
 * Finance2 routes.
 *
 * Mounted at /app/maud-erp/finance2 alongside — not in place of — the existing
 * finance-management routes. The option set mirrors the Finance module's menu
 * so both read the same way, plus the views that only Finance2 provides.
 *
 * Paths must match url_tx in migrations/009_finance2_full_option_set.sql.
 */

import React, { lazy } from 'react';
import type { RouteObject } from 'react-router-dom';
import { Navigate, Outlet } from 'react-router-dom';

// Setup reuses the Finance module's own screens rather than reimplementing them:
// the same DB-driven SetupPage (which resolves its cards from the URL path) and
// the same sixteen master components. Only the route prefix differs, so a master
// edited under Finance2 is the same record edited under Finance.
import { setupRoutes } from '../../../../core/setup/routes';
import { FINANCE_BANNER, ModuleBannerProvider } from '../../../../core/layout/moduleBanner';
import GlobalUdaHeaderSelect from '@/components/GlobalUdaHeaderSelect';

const Fund = lazy(() => import('../../finance-management/masters/fund'));
const Bank = lazy(() => import('../../finance-management/masters/bank'));
const FinancialYear = lazy(() => import('../../finance-management/masters/financial-year'));
const BankBranch = lazy(() => import('../../finance-management/masters/bank-branch'));
const BankAccount = lazy(() => import('../../finance-management/masters/bank-account'));
const Scheme = lazy(() => import('../../finance-management/masters/scheme'));
const SubScheme = lazy(() => import('../../finance-management/masters/sub-scheme'));
const FundSource = lazy(() => import('../../finance-management/masters/fund-source'));
const ChartOfAccounts = lazy(() => import('../../finance-management/masters/chart-of-accounts'));
const BudgetGroup = lazy(() => import('../../finance-management/masters/budget-group'));
const Functionary = lazy(() => import('../../finance-management/masters/functionary'));
const FunctionMaster = lazy(() => import('../../finance-management/masters/function'));
const FiscalPeriod = lazy(() => import('../../finance-management/masters/fiscal-period'));
const Supplier = lazy(() => import('../../finance-management/masters/supplier'));
const InstrumentType = lazy(() => import('../../finance-management/masters/instrument-type'));
const ChequeFormat = lazy(() => import('../../finance-management/masters/cheque-format'));

/*
 * Department and Designation come from Employee Management, not from
 * finance-management, and they are reused for exactly the same reason the
 * sixteen above are: a master edited under Finance2 must be the same record
 * edited anywhere else. Reimplementing an editor for `ds_dept_lst_t` here would
 * give the organisation two department lists that drift.
 *
 * They belong on this page because Finance reads them. The voucher form's
 * Department picker is populated from `ds_dept_lst_t` and the id it chooses is
 * written to `vouchermis.departmentid` — so a department missing from that
 * register is a voucher that cannot be filed against it, and until now the only
 * place to add one was a different module's setup screen.
 */
const Department = lazy(() => import('../../employee-management/masters/departments'));
const Designation = lazy(() => import('../../employee-management/masters/designations'));

/** Pass-through so the setup index can nest its own children. */
const PassThroughLayout = () => <Outlet />;

const Finance2Dashboard = lazy(() => import('../pages/Finance2Dashboard'));
const BudgetingPage = lazy(() => import('../pages/BudgetingPage'));
const ExpenseBillsPage = lazy(() => import('../pages/BillsPage').then((m) => ({ default: m.ExpenseBillsPage })));
const WorkBillsPage = lazy(() => import('../pages/BillsPage').then((m) => ({ default: m.WorkBillsPage })));
const PaymentsPage = lazy(() => import('../pages/PaymentsPage'));
const ReceiptPaymentsPage = lazy(() => import('../pages/ReceiptPaymentsPage'));
const IncomeExpenditurePage = lazy(() => import('../pages/IncomeExpenditurePage'));
const BalanceSheetPage = lazy(() => import('../pages/BalanceSheetPage'));
// Remittance is back (migration 101): the page is the collected-vs-banked panel with
// its own menu row, and each deposit now posts a contra voucher.
/*
 * Reports — the shared DB-driven list, the same screen Property Tax and Water
 * Tax use, so a finance report reads and exports like every other report in the
 * product.
 *
 * It resolves its options from `app_lst_t.app_url_tx` and the report tables.
 * That chain was intact but empty: the Finance2 app row exists (app_id 35,
 * '/app/maud-erp/finance2') and so does Finance Management (app_id 11), but NOT
 * ONE report was linked to either in `app_rpt_rel_t` — Property Tax had 27
 * registered, Water Tax 24, finance 0. The page was rendering "No reports found"
 * correctly; there was simply nothing to list.
 *
 * Ten finance reports are now registered by
 *   reports/migrations/001_register_finance_reports.sql
 * so this list is populated. Adding another finance report means another row in
 * that migration, not a change here.
 *
 * NOTE: ../pages/ReportsPage.tsx is a separate, code-defined report runner that
 * this route does NOT use. It is left in place but is unreferenced — do not
 * assume edits to it affect this option.
 */
const ReportsPage = lazy(() => import('../../../../core/reports/pages/ReportsList'));
const ReportDetailPage = lazy(() => import('../../../../core/reports/pages/ReportDetail'));
const ChartOfAccountsPage = lazy(() => import('../pages/ChartOfAccountsPage'));
const AccountExplorerPage = lazy(() => import('../pages/AccountExplorerPage'));
const CashPositionPage = lazy(() => import('../pages/CashPositionPage'));
const UdaComparisonPage = lazy(() => import('../pages/UdaComparisonPage'));
const VoucherExplorerPage = lazy(() => import('../pages/VoucherExplorerPage'));
const MyApprovalsPage = lazy(() => import('../pages/MyApprovalsPage'));
const InsightsPage = lazy(() => import('../pages/InsightsPage'));
const GlobalSearchPage = lazy(() => import('../pages/GlobalSearchPage'));

// -- Key Features views ------------------------------------------------------
const TrialBalancePage = lazy(() => import('../pages/TrialBalancePage'));
const ApprovalRoutePage = lazy(() => import('../pages/ApprovalRoutePage'));
const TallyReconPage = lazy(() => import('../pages/TallyReconPage'));
const SubLedgersPage = lazy(() => import('../pages/SubLedgersPage'));
const JournalEntriesPage = lazy(() => import('../pages/JournalEntriesPage'));
const CashFlowPage = lazy(() => import('../pages/CashFlowPage'));
const BankReconciliationPage = lazy(() => import('../pages/BankReconciliationPage'));
const RemittancePage = lazy(() => import('../pages/RemittancePage'));
const FundAccountingPage = lazy(() => import('../pages/FundAccountingPage'));
const InterUdaTransactionsPage = lazy(() => import('../pages/InterUdaTransactionsPage'));
const PeriodManagementPage = lazy(() => import('../pages/PeriodManagementPage'));
const OpeningBalancesPage = lazy(() => import('../pages/OpeningBalancesPage'));
const TallyEntryPage = lazy(() => import('../pages/TallyEntryPage'));
const StatementsChartOfAccountsPage = lazy(() => import('../pages/StatementsChartOfAccountsPage'));
const LedgerBookPage = lazy(() => import('../pages/AccountBooksPages').then((m) => ({ default: m.LedgerBookPage })));
const BankBookStatementPage = lazy(() => import('../pages/AccountBooksPages').then((m) => ({ default: m.BankBookStatementPage })));

export const finance2Routes: RouteObject[] = [
  { index: true, element: <Finance2Dashboard /> },

  // -- Options mirroring the Finance menu, in the same order ----------------
  { path: 'dashboard', element: <Finance2Dashboard /> },                 // Dashboard
  { path: 'budgeting', element: <BudgetingPage /> },                     // Budgeting
  // Budget Approval is now the "Budget books" section of the Inbox (one menu for
  // everything awaiting a decision). The path stays as a redirect for old links.
  { path: 'budget-approval', element: <Navigate to="../my-approvals?tab=budgets" replace /> },
  { path: 'expense-bills', element: <ExpenseBillsPage /> },              // Expense Bills
  { path: 'work-bills', element: <WorkBillsPage /> },                    // Work Bills
  { path: 'payments', element: <PaymentsPage /> },                       // Payments
  { path: 'receipt-payments', element: <ReceiptPaymentsPage /> },        // Receipt Payments
  { path: 'income-expenditure', element: <IncomeExpenditurePage /> },    // Income-Expenditure Statement
  { path: 'balance-sheet', element: <BalanceSheetPage /> },              // Balance Sheet
  /* The shared screens draw the Finance banner here, and only here (see moduleBanner). */
  { path: 'reports', element: <ModuleBannerProvider value={{ ...FINANCE_BANNER, title: 'Reports' }}><ReportsPage /></ModuleBannerProvider> },                         // Reports
  { path: 'reports/:id', element: <ModuleBannerProvider value={{ ...FINANCE_BANNER, title: 'Reports' }}><ReportDetailPage /></ModuleBannerProvider> },                // Reports · one report

  // -- Setup: the same screens Finance uses --------------------------------
  // The index is Finance's DB-driven SetupPage; it resolves its master cards
  // from the URL path, so the Finance2 options are registered against
  // app_url_tx '/app/maud-erp/finance2' in 011_finance2_setup_options.sql.
  { path: 'setup', element: <ModuleBannerProvider value={{ ...FINANCE_BANNER, title: 'Setup', udaSlot: <GlobalUdaHeaderSelect /> }}><PassThroughLayout /></ModuleBannerProvider>, children: setupRoutes },
  { path: 'setup/approval-route', element: <ApprovalRoutePage /> },          // Maker → Checker → Approver, by department and designation
  { path: 'setup/fund', element: <Fund /> },
  { path: 'setup/bank', element: <Bank /> },
  { path: 'setup/financial-year', element: <FinancialYear /> },
  { path: 'setup/bank-branch', element: <BankBranch /> },
  { path: 'setup/bank-account', element: <BankAccount /> },
  { path: 'setup/scheme', element: <Scheme /> },
  { path: 'setup/sub-scheme', element: <SubScheme /> },
  { path: 'setup/fund-source', element: <FundSource /> },
  { path: 'setup/chart-of-accounts', element: <ChartOfAccounts /> },
  { path: 'setup/budget-group', element: <BudgetGroup /> },
  { path: 'setup/functionary', element: <Functionary /> },
  { path: 'setup/function', element: <FunctionMaster /> },
  { path: 'setup/fiscal-period', element: <FiscalPeriod /> },
  { path: 'setup/supplier', element: <Supplier /> },
  { path: 'setup/instrument-type', element: <InstrumentType /> },
  { path: 'setup/cheque-format', element: <ChequeFormat /> },
  // Organisation masters the finance path depends on — see the note by the
  // imports. Registered as setup options by migration 075.
  { path: 'setup/department', element: <Department /> },
  { path: 'setup/designation', element: <Designation /> },

  // -- Finance2-only views --------------------------------------------------
  // The ledger flow, in order: the chart of accounts is the structure, the
  // General Ledger is that structure with the period's money in it, and the
  // sub-ledgers and trial balance are branches and positions of the same thing.
  // Note this is /chart-of-accounts, distinct from setup/chart-of-accounts —
  // that one is the master editor, this one is the reading view.
  { path: 'chart-of-accounts', element: <StatementsChartOfAccountsPage /> }, // Chart of Accounts (Financial Statements tab, Dashboard style)
  { path: 'chart-of-accounts-classic', element: <ChartOfAccountsPage /> }, // the older analysis page, kept unlinked
  // Financial Statements › Ledger and › Bank Book — the Tally-mode ledger screens on a page.
  { path: 'ledger', element: <LedgerBookPage /> },
  { path: 'bank-book', element: <BankBookStatementPage /> },
  { path: 'account-explorer', element: <AccountExplorerPage /> },        // General Ledger
  { path: 'cash-position', element: <CashPositionPage /> },              // Bank Book
  { path: 'uda-comparison', element: <UdaComparisonPage /> },            // UDA Comparison
  { path: 'vouchers', element: <VoucherExplorerPage /> },                // Day Book
  { path: 'tally-entry', element: <TallyEntryPage /> },                  // Voucher Entry (Tally mode)

  /*
   * The approval inbox. It is not a reading of the ledger like its neighbours —
   * it is the ONLY place a voucher awaiting approval appears at all. Every other
   * page in this module filters `vh.status = 0`, which pending vouchers fail by
   * design, so without this route the work would be invisible to the only person
   * who can move it. Path must match url_tx in migration 081.
   */
  { path: 'my-approvals', element: <MyApprovalsPage /> },                // Inbox (sidebar)
  { path: 'vouchers/approvals', element: <MyApprovalsPage inVouchers /> }, // Voucher Management › My Approvals                // My Approvals
  { path: 'insights', element: <InsightsPage /> },                       // Insights & Alerts
  // Search every source that posts into the books, and trace a hit end to end.
  // Path must match url_tx in migrations/105_finance2_global_search_menu.sql.
  { path: 'search', element: <GlobalSearchPage /> },                     // Global Search

  // -- Key Features options -------------------------------------------------
  // Paths must match url_tx in migrations/013_finance2_key_features.sql.
  { path: 'trial-balance', element: <TrialBalancePage /> },              // Trial Balance
  { path: 'journal-entries', element: <JournalEntriesPage /> },          // Journal Entries
  { path: 'bank-reconciliation', element: <BankReconciliationPage /> },  // Bank Reconciliation
  { path: 'remittance', element: <RemittancePage /> },                   // Remittance
  { path: 'cash-flow', element: <CashFlowPage /> },                      // Financial Statements · Cash Flow
  { path: 'fund-accounting', element: <FundAccountingPage /> },          // Fund Accounting
  { path: 'inter-uda', element: <InterUdaTransactionsPage /> },          // Inter-UDA Transactions
  { path: 'period-management', element: <PeriodManagementPage /> },      // Period Management

  // The balances the authorities held before their books began. Not a reading of
  // the ledger but an input to it: Tally carries no master openings (README §53),
  // so the figures come off bank statements and are posted from here.
  // Path must match url_tx in migrations/045_finance2_opening_balances_menu.sql.
  { path: 'opening-balances', element: <OpeningBalancesPage /> },        // Opening Balances

  // Not a reading of the ledger like the others — a check ON it. Our live books
  // against the Tally companies they were imported from, with the snapshot's
  // capture date stated so the comparison cannot be mistaken for current when
  // it is not.
  { path: 'tally-recon', element: <TallyReconPage /> },                  // Tally Reconciliation

  // Financial Statements is a family, not a page: the menu points straight at
  // its first member and the four are tabs on each other. This path is kept as a
  // redirect so older links and the previous menu url still land somewhere.
  { path: 'financial-statements', element: <Navigate to="../balance-sheet" replace /> },

  // Sub-Ledgers is one page with four ledgers; the kind travels in the path so
  // each tab is a real URL. The bare path opens payables.
  { path: 'sub-ledgers', element: <SubLedgersPage /> },
  { path: 'sub-ledgers/:kind', element: <SubLedgersPage /> },
];

export default finance2Routes;
